import React, { useState, useEffect } from 'react';
import { FiPlusCircle, FiTag } from 'react-icons/fi';
import { Header } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import AddCategoryModal from '../../components/Finance/AddCategoryModal';

const ExpenseCategories = () => {
  useKYCStatusRedirect('/finance/expense-categories');
  const { school } = useStateContext();
  const schoolId = school?._id;

  const [categories, setCategories] = useState({ system: [], custom: [] });
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const fetchCategories = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const { data } = await userRequest.get(`/schools/${schoolId}/finance/expense-categories`);
      setCategories({ system: data.system || [], custom: data.custom || [] });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const toggleActive = async (slug, isActive) => {
    try {
      await userRequest.patch(`/schools/${schoolId}/finance/expense-categories/${slug}`, { is_active: !isActive });
      fetchCategories();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex items-start justify-between mb-6">
        <Header category="Finance" title="Expense Categories" />
        <button
          type="button"
          onClick={() => setIsAddOpen(true)}
          className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
        >
          <FiPlusCircle className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {loading ? (
        <div className="py-14 text-center text-sm text-gray-400">Loading categories...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-700 mb-4">System Categories</p>
            <div className="space-y-2">
              {categories.system.length === 0 ? (
                <p className="text-sm text-gray-400">No system categories.</p>
              ) : categories.system.map(cat => (
                <div key={cat.slug} className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50">
                  <FiTag className="text-gray-400 shrink-0" />
                  <span className="text-sm text-gray-700">{cat.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-gray-200 rounded-2xl p-5">
            <p className="text-sm font-semibold text-gray-700 mb-4">Custom Categories</p>
            <div className="space-y-2">
              {categories.custom.length === 0 ? (
                <p className="text-sm text-gray-400">No custom categories yet.</p>
              ) : categories.custom.map(cat => (
                <div key={cat.slug} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-gray-50">
                  <div className="flex items-center gap-2">
                    <FiTag className="text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700">{cat.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleActive(cat.slug, cat.is_active)}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${
                      cat.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                  >
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isAddOpen && (
        <AddCategoryModal
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
          schoolId={schoolId}
          onCreated={fetchCategories}
        />
      )}
    </div>
  );
};

export default ExpenseCategories;
